import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { ProjectTasksFiltersComponent } from '@project-modules/project-tasks/pages/project-tasks/components/project-tasks-filters/project-tasks-filters.component';
import { ListConfig } from '@shared/modules/list/interfaces/list-config.interface';
import { cloneDeep } from 'lodash';
import { DefaultListConfig } from '@shared/modules/list/configs/default-list.config';
import { ProjectTasksListConfig } from '@project-modules/project-tasks/shared/configs/project-tasks-list.config';
import { ProjectPreviewService } from '@project-modules/project-preview/services/project-preview.service';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { ListService } from '@shared/modules/list/services/list.service';
import { StorageService } from '@core/services/storage.service';
import { Config } from '@shared/configs/config';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { IProjectPreviewEvent } from '@project-modules/project-preview/interfaces/project-preview-event.interface';
import { ProjectPreviewEventType } from '@project-modules/project-preview/enums/projcet-preview-event-type.enum';
import { SettingsService } from '@modules/settings/shared/services/settings.service';
import { ActivatedRoute } from '@angular/router';
import { TaskType } from '@shared/enums/task-type.enum';
import { tap } from 'rxjs/operators';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { Task } from '@shared/models/task.model';
import { TaskGroup } from '@shared/models/task-group.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { ListTasksTaskStatusEvent } from '@shared/modules/list/components/list-tasks/components/list-tasks-group/list-tasks-group.component';
import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { TaskController } from '@shared/controllers/task.controller';
import { SnackBarService } from '@core/services/snackbar.service';
import { SidenavService } from '@shared/services/sidenav/sidenav.service';
import { ProcessMessageTaskSidenavComponent } from '@modules/projects/modules/project-dashboard/pages/project-stage-tasks/components/process-message-task-sidenav/process-message-task-sidenav.component';
import { ListTasksTemplateAddEvent } from '@shared/modules/list/components/list-tasks/components/list-tasks-group/list-tasks-group.component';
import { ProjectDashboardStepperSyncService } from '@modules/projects/modules/project-dashboard/services/project-dashboard-stepper-sync.service';
import { ProjectStageAPIService } from '@modules/projects/shared/services/project-stage-api.service';
import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { UIModule } from '@shared/modules/ui/ui.module';
import { ListModule } from '@shared/modules/list/list.module';
import { ProjectsModule } from '@modules/projects/projects.module';
import { ListUrlController } from '@shared/modules/list/controllers/list-url.controller';
import { ListEventType } from '@shared/modules/list/model/list-event.model';
import { MatDialog } from '@angular/material/dialog';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import {
  ListTasksGroupOrderEvent,
  ListTasksComponent
} from '@shared/modules/list/components/list-tasks/list-tasks.component';
import { TaskGroupModalComponent } from '@shared/modules/task-sidenav/components/task-group-modal/task-group-modal.component';
import { ListTasksGroupEditEvent, ListTasksTaskDropEvent } from '@shared/modules/list/components/list-tasks/components/list-tasks-group/list-tasks-group.component';
import {
  ListTasksProcessInsertBus,
  ListTasksProcessInsertEvent
} from '@shared/modules/list/components/list-tasks/components/list-tasks-group/list-tasks-process-insert.bus';
import { ProjectTaskColumnsMenuComponent } from '../../components/project-task-columns-menu/project-task-columns-menu.component';

@Component({
  styleUrls: ['./project-tasks.component.scss'],
  templateUrl: './project-tasks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SettingsService, ListService, ListUrlController, ListTasksProcessInsertBus],
  standalone: true,
  imports: [SharedModule, UIModule, ListModule, ProjectsModule, ListTasksComponent, ProjectTaskColumnsMenuComponent]
})
export class ProjectTasksComponent implements OnInit {
  public readonly projectPreviewService = inject(ProjectPreviewService);
  public readonly projectTaskApiService = inject(ProjectTaskAPIService);
  public readonly listTaskService = inject(ListTaskService);
  public readonly listService = inject(ListService);
  private readonly storageService = inject(StorageService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly projectStore = inject(ProjectStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly snackBarService = inject(SnackBarService);
  private readonly sidenavService = inject(SidenavService);
  private readonly projectStageApiService = inject(ProjectStageAPIService);
  private readonly stepperSyncService = inject(ProjectDashboardStepperSyncService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly taskService = inject(TaskService);
  private readonly processInsertBus = inject(ListTasksProcessInsertBus);

  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  ProjectTasksFiltersComponent = ProjectTasksFiltersComponent;
  taskSidenavCtrl: TaskSidenavController;
  config: ListConfig;
  taskType: TaskType;
  taskTypeName: string;

  get shouldShowProcessTasks(): boolean {
    return this.taskType === TaskType.TYPE_TASK;
  }

  project = computed<Project | null>(() => this.projectStore.project() || null);
  projectStage = computed<ProjectStage | null>(() => (this.project()?.stage as ProjectStage) || null);
  stageTitle = computed(() => {
    const stage = this.projectStage();
    return stage ? this.translateService.instant(`Projects.Stage.${stage}`) : '';
  });
  readonly processAccordionStorageKey = computed(() => {
    const projectId = this.project()?.id;
    return projectId ? 'project-tasks-process-groups:' + projectId : null;
  });
  templateGroups = signal<ListGroupTask[]>([]);
  templateLoading = signal(false);
  generalGroups = signal<ListGroupTask[]>([]);
  generalLoading = signal(false);
  private initializedProjectId: number | null = null;

  constructor() {
    effect(
      () => {
        const project = this.project();
        if (!project?.id) return;
        this.initListConfig();
        this.setCompleted();
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const projectId = this.project()?.id ?? null;
      const projectStage = this.projectStage();

      queueMicrotask(() => {
        if (!this.shouldShowProcessTasks || !projectId || !projectStage) {
          this.templateGroups.set([]);
          this.templateLoading.set(false);
          return;
        }

        this.loadTemplateTasks(projectId, projectStage, true);
      });
    });
  }

  ngOnInit() {
    this.taskSidenavCtrl = new TaskSidenavController();
    this.taskType = this.activatedRoute.snapshot.data.type;
    this.taskTypeName = this.getTaskTypeName(this.taskType);
    this.initActionSubscription();
    this.listenListServiceEvents();
    this.listenListDataEvents();
    this.listenProjectPreviewEvents();
    this.listenProcessInsertEvents();
  }

  private initActionSubscription(): void {
    this.projectPreviewService.actionEvent$
      .pipe(
        tap(() => this.createNewTask()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private resolveShowCompletedCheckbox(): number {
    const userPerson = this.storageService.UserPerson;
    return userPerson && userPerson.showDoneTasks ? 1 : 0;
  }

  listenProjectPreviewEvents() {
    this.projectPreviewService.projectServiceEventEmitter
      .pipe(
        tap(({ type }: IProjectPreviewEvent) => {
          switch (type) {
            case ProjectPreviewEventType.CHANGE_STAGE:
              this.listService.getRows();
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenListServiceEvents(): void {
    this.listTaskService.emitter
      .pipe(
        tap(({ type, data }) => {
          const projectId = this.project()?.id;
          const projectStage = this.projectStage();
          if (!projectId) return;

          switch (type) {
            case ListTaskEventType.UPDATE_TASK_LIST:
              this.refreshAllTasks(projectId, projectStage);
              return;
            case ListTaskEventType.ADD_TASK:
              if (data?.parentId || data?.task?.parentId) {
                this.refreshAllTasks(projectId, projectStage);
                return;
              }
              if (this.isProcessTaskEvent(data)) {
                this.loadTemplateTasks(projectId, projectStage, false);
              } else {
                this.listService.getRows();
              }
              return;
            case ListTaskEventType.ADD_SUB_TASK:
              this.refreshAllTasks(projectId, projectStage);
              return;
            case ListTaskEventType.UPDATE_TASK:
            case ListTaskEventType.REMOVE_TASK:
              if (this.isProcessTaskEvent(data)) {
                this.loadTemplateTasks(projectId, projectStage, false);
              } else {
                this.listService.getRows();
              }
              return;
            default:
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private listenListDataEvents(): void {
    this.listService.eventEmitter
      .pipe(
        tap(({ type }) => {
          if (type === ListEventType.START_GET_ROWS) {
            this.generalLoading.set(true);
            return;
          }

          if (type === ListEventType.END_GET_ROWS) {
            const openedTaskIds = this.collectOpenedTaskIds(this.generalGroups());
            const rows = Array.isArray(this.listService.rows) ? this.listService.rows : [];
            this.generalGroups.set(this.normalizeApiGroups(rows, false, openedTaskIds));
            this.generalLoading.set(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  createNewTask(): void {
    this.taskSidenavCtrl.addTask({
      projectID: this.project()?.id,
      taskType: this.taskType
    });
  }

  createNewTaskGroup(): void {
    const dialogRef = this.taskSidenavCtrl.addTaskGroup(this.project()?.id, this.taskType);

    dialogRef.componentInstance.onGroupAdd
      .pipe(
        tap(() => this.listService.getRows()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onTemplateTaskStatusChanged(event: ListTasksTaskStatusEvent): void {
    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    if (!projectId || !projectStage) return;

    const ctrl = new TaskController(event.task);
    const applyStatusToSubtasks = event.applyStatusToSubtasks ?? event.subtasksCompleted;
    const completeSubtasks$ = this.syncSubtasksStatus(event.task, applyStatusToSubtasks);

    completeSubtasks$
      .pipe(
        switchMap(() => ctrl.changeTaskStatus(event.task)),
        switchMap(() => this.projectStageApiService.getProjectStages(projectId)),
        tap((stages) => {
          this.stepperSyncService.updateProjectStages(projectId, stages || []);
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorStatusChange'));
          return of(null);
        }),
        finalize(() => this.loadTemplateTasks(projectId, projectStage, false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.snackBarService.success(this.getStatusChangedMessage(event.task));
      });
  }

  onTemplateTaskClicked(event: { task: Task; groupId: number; isProcessGroup: boolean }): void {
    const taskType = String((event?.task as any)?.type || '');
    if (
      !event?.isProcessGroup ||
      (taskType !== TaskType.TYPE_MESSAGE && taskType !== TaskType.TYPE_FOLLOW_UP)
    ) {
      return;
    }

    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    const task = event.task;
    if (!projectId || !projectStage || !task?.id) return;

    this.sidenavService.open(
      ProcessMessageTaskSidenavComponent,
      this.taskSidenavCtrl.getWideSidenavConfig(),
      {
        mode: 'send',
        projectId,
        stage: projectStage,
        sortOrder: Number(task.sortOrder || 1),
        parentId: task.parentId ?? null,
        groupId: event.groupId,
        mapperTaskId: task.id,
        taskType,
        task
      }
    );
  }

  onTemplateAddSelected(event: ListTasksTemplateAddEvent): void {
    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    if (!projectId || !projectStage) return;

    const group = event.group;
    const sortOrder =
      (group?.tasks?.length || 0) +
      (group?.completedTasks?.length || 0) +
      (group?.cancelledTasks?.length || 0) +
      1;

    if (event.type === TaskType.TYPE_MESSAGE || event.type === TaskType.TYPE_FOLLOW_UP) {
      const mapperTaskId = Number(
        group?.tasks?.[0]?.id || group?.completedTasks?.[0]?.id || group?.cancelledTasks?.[0]?.id || 0
      );

      this.sidenavService.open(
        ProcessMessageTaskSidenavComponent,
        this.taskSidenavCtrl.getWideSidenavConfig(),
        {
          projectId,
          stage: projectStage,
          sortOrder,
          parentId: null,
          groupId: group?.taskGroup?.id || null,
          mapperTaskId: mapperTaskId > 0 ? mapperTaskId : null,
          taskType: event.type
        }
      );
      return;
    }

    this.taskSidenavCtrl.addTask({
      projectID: projectId,
      groupType: 'activity-template',
      taskType: event.type as TaskType,
      taskGroupId: group?.taskGroup?.id,
      extra: {
        type: event.type,
        isProcessTaskContext: true,
        activityTemplateTaskType: event.type,
        activityTemplateContext: {
          stage: projectStage,
          sortOrder,
          parentId: null,
          groupId: group?.taskGroup?.id || null
        }
      }
    });
  }

  private onProcessInsertSelected(event: ListTasksProcessInsertEvent): void {
    const projectId = this.project()?.id;
    const projectStage = this.projectStage();
    if (!projectId || !projectStage) return;

    const tasks = [...(event.group?.tasks || [])].sort(
      (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
    );
    const insertIndex = Math.max(0, Math.min(event.insertIndex, tasks.length));
    const targetTask = tasks[insertIndex];
    const sortOrder = targetTask?.sortOrder ? Number(targetTask.sortOrder) : insertIndex + 1;
    const preferredTask = targetTask || tasks[insertIndex - 1] || tasks[0];
    const mapperTaskId = Number(preferredTask?.id || 0) || null;
    const groupId = event.group?.taskGroup?.id || null;

    if (event.type === 'message') {
      this.sidenavService.open(
        ProcessMessageTaskSidenavComponent,
        this.taskSidenavCtrl.getWideSidenavConfig(),
        {
          mode: 'create-and-send',
          projectId,
          stage: projectStage,
          sortOrder,
          parentId: null,
          groupId,
          mapperTaskId,
          taskType: TaskType.TYPE_MESSAGE
        }
      );
      return;
    }

    this.taskSidenavCtrl.addTask({
      projectID: projectId,
      groupType: 'activity-template',
      taskType: TaskType.TYPE_TASK,
      taskGroupId: groupId,
      extra: {
        type: TaskType.TYPE_TASK,
        isProcessTaskContext: true,
        activityTemplateTaskType: TaskType.TYPE_TASK,
        activityTemplateContext: {
          stage: projectStage,
          sortOrder,
          parentId: null,
          groupId
        }
      }
    });
  }

  onGeneralTaskStatusChanged(event: ListTasksTaskStatusEvent): void {
    const ctrl = new TaskController(event.task);
    const applyStatusToSubtasks = event.applyStatusToSubtasks ?? event.subtasksCompleted;
    const completeSubtasks$ = this.syncSubtasksStatus(event.task, applyStatusToSubtasks);

    completeSubtasks$
      .pipe(
        switchMap(() => ctrl.changeTaskStatus(event.task)),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Tasks.errorStatusChange'));
          return of(null);
        }),
        finalize(() => this.listService.getRows()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.snackBarService.success(this.getStatusChangedMessage(event.task));
      });
  }

  private syncSubtasksStatus(task: Task, applyStatusToSubtasks?: boolean) {
    if (!applyStatusToSubtasks) return of(null);

    if (task?.status === TaskStatus.COMPLETED) {
      return new TaskController(task).completeSubtasks(task);
    }

    const subtasks = this.collectDescendantTasks(task);
    if (!subtasks.length) return of(null);

    return forkJoin(
      subtasks.map((subtask) => {
        const ctrl = new TaskController(subtask);
        return ctrl.changeTaskStatus(subtask);
      })
    );
  }

  private collectDescendantTasks(task: Task): Task[] {
    const descendants: Task[] = [];

    const collect = (parent: Task): void => {
      (parent?.children || []).forEach((child) => {
        descendants.push(child);
        collect(child);
      });
    };

    collect(task);
    return descendants;
  }

  onGeneralTaskDrop(event: ListTasksTaskDropEvent): void {
    const moveGroup$ =
      event.sourceGroupId !== event.targetGroupId
        ? this.projectTaskApiService.taskGroupChange(event.taskId, event.targetGroupId)
        : of(null);

    moveGroup$
      .pipe(
        switchMap(() => this.projectTaskApiService.taskOrderChange(event.taskId, event.index + 1)),
        catchError(() => of(null)),
        finalize(() => this.listService.getRows()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onGeneralGroupOrderChanged(event: ListTasksGroupOrderEvent): void {
    const projectId = this.project()?.id;
    if (!projectId) return;

    this.projectTaskApiService
      .taskGroupBulkEdit({
        groupsOrders: event.groups,
        projectId,
        type: this.taskType
      })
      .pipe(
        catchError(() => of(null)),
        finalize(() => this.listService.getRows()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onGeneralGroupEditRequested(event: ListTasksGroupEditEvent): void {
    const projectId = this.project()?.id;
    const group = event.group?.taskGroup;
    if (!projectId || !group?.id) return;

    const dialogRef = this.dialog.open(TaskGroupModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      autoFocus: true,
      data: { notSendNewGroup: true, edit: true, taskGroup: group }
    });

    dialogRef.componentInstance.group.setValue(group.name);

    const sub = dialogRef.componentInstance.onGroupAdd.subscribe((groupName: string) => {
      this.taskService
        .editTaskGroup(groupName, group.id)
        .pipe(
          catchError(() => {
            this.snackBarService.error(this.translateService.instant('errorDefault'));
            return of(null);
          }),
          finalize(() => this.listService.getRows()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => sub.unsubscribe());
    });

    const removeSub = dialogRef.componentInstance.onGroupRemove.subscribe((taskGroup: TaskGroup) => {
      this.projectTaskApiService
        .taskGroupBulkEdit({
          groupsToDeleteIds: [taskGroup.id],
          projectId,
          type: this.taskType
        })
        .pipe(
          catchError(() => {
            this.snackBarService.error(this.translateService.instant('errorDefault'));
            return of(null);
          }),
          finalize(() => this.listService.getRows()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => removeSub.unsubscribe());
    });
  }

  onAddTaskToGeneral(group: ListGroupTask): void {
    const projectId = this.project()?.id;
    if (!projectId) return;

    this.taskSidenavCtrl.addTask({
      projectID: projectId,
      taskGroupId: group.taskGroup?.id,
      taskType: this.taskType
    });
  }

  private listenProcessInsertEvents(): void {
    this.processInsertBus.insertSelected$
      .pipe(
        tap((event) => this.onProcessInsertSelected(event)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initListConfig(): void {
    const projectId = this.project()?.id;
    if (!projectId) return;

    if (this.initializedProjectId !== projectId) {
      this.listService.clearService();
      this.initializedProjectId = projectId;
    }

    this.config = cloneDeep(DefaultListConfig);
    const listConfig: ListConfig = {
      ...ProjectTasksListConfig,
      url: `${Config.API}/project/${projectId}/task`
    };
    this.config = Object.assign(this.config, listConfig);
    this.listService.config = this.config;
    this.listService.config.limit = this.listService.PerPage as number;
    this.listService.setParams('type', this.taskType || TaskType.TYPE_TASK);
    this.listService.data['projectId'] = projectId;
    this.cdr.detectChanges();
  }

  private setCompleted(): void {
    const showDoneTasks = this.resolveShowCompletedCheckbox();
    this.listService.setFilter('[show_completed]', showDoneTasks ? 1 : 0);
    if (this.listService.config) {
      this.listService.setPage(1);
      this.listService.getRows();
    }
  }

  onChangeFilter(): void {
    Object.keys(this.listService.filtersForm.value).forEach((key: string) => {
      let value = this.listService.filtersForm.value[key];
      Array.isArray(value) && !value.length ? (value = null) : null;
      this.listService.setFilter(key, value);
    });
    this.listService.getRows();
  }

  private getTaskTypeName(taskType: TaskType): string {
    switch (taskType) {
      case TaskType.TYPE_TASK:
        return 'tasks';
      case TaskType.TYPE_CHANGE:
        return 'changes';
      case TaskType.TYPE_CORRECTION:
        return 'corrections';
      case TaskType.TYPE_ORDER:
        return 'orders';
      default:
        return 'tasks';
    }
  }

  private loadTemplateTasks(
    projectId: number | null,
    stage: ProjectStage | null,
    withLoader: boolean = true
  ): void {
    if (!this.shouldShowProcessTasks || !projectId || !stage) {
      this.templateGroups.set([]);
      return;
    }

    const openedTaskIds = this.collectOpenedTaskIds(this.templateGroups());

    if (withLoader) this.templateLoading.set(true);

    this.projectTaskApiService
      .getProjectActivityTasks(projectId, stage)
      .pipe(
        map((rows) => this.normalizeApiGroups(rows || [], true, openedTaskIds)),
        tap((groups) => this.templateGroups.set(groups)),
        catchError(() => {
          this.templateGroups.set([]);
          return of([]);
        }),
        finalize(() => {
          if (withLoader) this.templateLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private refreshAllTasks(projectId: number, projectStage: ProjectStage | null): void {
    if (this.shouldShowProcessTasks) {
      this.loadTemplateTasks(projectId, projectStage, false);
    }
    this.listService.getRows();
  }

  private normalizeApiGroups(
    rows: ListGroupTask[],
    isProcessGroup: boolean,
    openedTaskIds: Set<number> = new Set<number>()
  ): ListGroupTask[] {
    return rows
      .map((row) => {
        const taskGroup = new TaskGroup(row.taskGroup);

        const tasks = (row.tasks || [])
          .map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        const completedTasks = (row.completedTasks || [])
          .map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        const cancelledTasks = (row.cancelledTasks || [])
          .map((task) => this.normalizeTask(task, taskGroup, openedTaskIds))
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        return {
          ...row,
          data: {
            ...(row as any)?.data,
            isProcessGroup
          },
          taskGroup,
          tasks,
          completedTasks,
          cancelledTasks
        } as ListGroupTask;
      })
      .sort((a, b) => (a.taskGroup?.sortOrder || 0) - (b.taskGroup?.sortOrder || 0));
  }

  private normalizeTask(task: Task, taskGroup: TaskGroup, openedTaskIds: Set<number>): Task {
    const children = ((task as any)?.children || [])
      .map((child: Task) => this.normalizeTask(child, taskGroup, openedTaskIds))
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    const normalizedTask = new Task({ ...(task as any), taskGroup, children } as any);
    const safeTaskId = Number(normalizedTask?.id || 0);
    const visibleChildren = (children || []).filter((child) => !child?.deleted);

    normalizedTask.children = children;
    normalizedTask.childrenCount = visibleChildren.length;
    normalizedTask.completedChildrenCount = visibleChildren.filter(
      (child) => child?.status === TaskStatus.COMPLETED
    ).length;

    if (safeTaskId > 0) normalizedTask.openedSubtasks = openedTaskIds.has(safeTaskId);
    else normalizedTask.openedSubtasks = !!normalizedTask.openedSubtasks;

    return normalizedTask;
  }

  private collectOpenedTaskIds(groups: ListGroupTask[]): Set<number> {
    const ids = new Set<number>();

    const collectFromTask = (task: Task): void => {
      const taskId = Number(task?.id || 0);
      if (taskId > 0 && !!task?.openedSubtasks) {
        ids.add(taskId);
      }
      (task?.children || []).forEach((child) => collectFromTask(child));
    };

    (groups || []).forEach((group) => {
      (group?.tasks || []).forEach((task) => collectFromTask(task));
      (group?.completedTasks || []).forEach((task) => collectFromTask(task));
      (group?.cancelledTasks || []).forEach((task) => collectFromTask(task));
    });

    return ids;
  }

  private isProcessTaskEvent(data: any): boolean {
    if (data?._isActivityTemplate || data?.task?._isActivityTemplate) {
      return true;
    }

    const taskLike = data?.task || data;
    const taskGroupId = Number(taskLike?.taskGroup?.id);
    if (!taskGroupId) return false;

    return this.templateGroups().some((group) => Number(group?.taskGroup?.id) === taskGroupId);
  }

  private getStatusChangedMessage(task: Task): string {
    const rawType = task?.type || 'task';
    const normalizedType = rawType === 'company-task' || rawType === 'client-task' ? 'task' : rawType;
    const actionKey = task?.status === TaskStatus.COMPLETED ? 'completed' : 'created';

    return this.translateService.instant('Tasks.successStatusChangeAction', {
      taskType: this.translateService.instant(`Tasks.TaskType.${normalizedType}`),
      action: this.translateService.instant(`Tasks.statusAction.${actionKey}`)
    });
  }
}
